import type { EconomyFns } from './economy';
import type { MultiplierFns } from './multipliers';
import type { EventBus } from './events';
import type { GameState, StationState } from './state';
import { isHeroHome } from './state';
import { now } from './clock';

export interface SimContext {
  economy: EconomyFns;
  multipliers: MultiplierFns;
  /** Wall clock ms; defaults to injectable clock. */
  nowMs?: () => number;
  events?: EventBus;
}

export type Bottleneck = 'mining' | 'lift' | 'warehouse' | 'none';

function stationActive(station: StationState, atMs: number): boolean {
  return station.manager || atMs < station.cycleEndsAt;
}

/** Trait/badge only when managers registry has an entry; else managed rate = 1. */
function managerMeta(
  state: GameState,
  id: string,
): { hasTrait: boolean; badgeLevel: number } {
  const m = state.managers[id];
  if (!m) return { hasTrait: false, badgeLevel: 1 };
  return { hasTrait: true, badgeLevel: Math.max(1, m.level) };
}

function stationRateMult(
  station: StationState,
  atMs: number,
  state: GameState,
  managerId: string,
  ctx: SimContext,
): number {
  if (!stationActive(station, atMs)) return 0;
  if (station.manager) {
    const meta = managerMeta(state, managerId);
    return ctx.multipliers.stationMultiplier({
      managed: true,
      hasTrait: meta.hasTrait,
      badgeLevel: meta.badgeLevel,
      rallyActive: atMs < station.abilityEndsAt,
    });
  }
  // Unmanaged tapped cycle — efficiency from config; no Rally.
  return ctx.economy.config.managers.unmanagedActiveEfficiency;
}

/** Instantaneous managed ore/sec links (unmanaged = 0). For bottleneck UI / offline. */
export function managedRates(
  state: GameState,
  ctx: SimContext,
  atMs: number = now(),
): {
  mining: number;
  lift: number;
  warehouse: number;
  throughput: number;
  bottleneck: Bottleneck;
} {
  const eco = ctx.economy;
  let mining = 0;
  for (let i = 0; i < state.site.shafts.length; i++) {
    const shaft = state.site.shafts[i]!;
    if (shaft.level <= 0 || !shaft.manager) continue;
    const mult = stationRateMult(shaft, atMs, state, `shaft:${i}`, ctx);
    mining += eco.shaftProd(i + 1, shaft.level, state.site.index) * mult;
  }
  const elev = state.site.elevator;
  const lift = elev.manager
    ? eco.elevatorCap(Math.max(1, elev.level)) *
      stationRateMult(elev, atMs, state, 'elevator', ctx)
    : 0;
  const wh = state.site.warehouse;
  const warehouse = wh.manager
    ? eco.warehouseCap(Math.max(1, wh.level)) *
      stationRateMult(wh, atMs, state, 'warehouse', ctx)
    : 0;
  const throughput = Math.min(mining, lift, warehouse);
  let bottleneck: Bottleneck = 'none';
  if (throughput <= 0) bottleneck = 'none';
  else if (mining <= lift && mining <= warehouse) bottleneck = 'mining';
  else if (lift <= warehouse) bottleneck = 'lift';
  else bottleneck = 'warehouse';
  return { mining, lift, warehouse, throughput, bottleneck };
}

/**
 * Advance simulation by dtSeconds (conceptual 10 Hz → dt 0.1).
 * Tick order: shafts → lift (shallowest first) → trading post → gold/events.
 */
export function tick(state: GameState, dtSeconds: number, ctx: SimContext): void {
  if (dtSeconds <= 0) return;
  const atMs = (ctx.nowMs ?? now)();
  const eco = ctx.economy;

  // 1. Shafts → piles
  for (let i = 0; i < state.site.shafts.length; i++) {
    const shaft = state.site.shafts[i]!;
    if (shaft.level <= 0) continue;
    const mult = stationRateMult(shaft, atMs, state, `shaft:${i}`, ctx);
    if (mult <= 0) continue;
    shaft.pile += eco.shaftProd(i + 1, shaft.level, state.site.index) * mult * dtSeconds;
  }

  // 2. Lift: shallowest piles first → surfacePile
  const elev = state.site.elevator;
  const elevMult = stationRateMult(elev, atMs, state, 'elevator', ctx);
  let liftBudget =
    elevMult > 0
      ? eco.elevatorCap(Math.max(1, elev.level)) * elevMult * dtSeconds
      : 0;
  if (liftBudget > 0) {
    for (let i = 0; i < state.site.shafts.length && liftBudget > 0; i++) {
      const shaft = state.site.shafts[i]!;
      if (shaft.pile <= 0) continue;
      const take = Math.min(shaft.pile, liftBudget);
      shaft.pile -= take;
      state.site.surfacePile += take;
      liftBudget -= take;
    }
  }

  // 3. Trading Post: surfacePile → gold
  const wh = state.site.warehouse;
  const whMult = stationRateMult(wh, atMs, state, 'warehouse', ctx);
  const sellBudget =
    whMult > 0
      ? eco.warehouseCap(Math.max(1, wh.level)) * whMult * dtSeconds
      : 0;
  const sold = Math.min(state.site.surfacePile, Math.max(0, sellBudget));
  if (sold > 0) {
    state.site.surfacePile -= sold;
    const valueMult = ctx.multipliers.valueMultiplier({
      siteIndex: state.site.index,
      shrineRichVeins: state.shrine['richVeins'] ?? 0,
      heroHome: isHeroHome(state, atMs),
      goldenLantern: state.entitlements.goldenLantern,
      adBoostActive: atMs < state.boosts.adBoostEndsAt,
    });
    const goldGain = sold * valueMult;
    state.currencies.gold += goldGain;
    state.site.earnedThisSite += goldGain;
    state.stats.lifetimeGold += goldGain;
    ctx.events?.emit('goldChanged', { gold: state.currencies.gold, delta: goldGain });
  }

  const rates = managedRates(state, ctx, atMs);
  ctx.events?.emit('bottleneckChanged', { bottleneck: rates.bottleneck });
}
